import { useEffect, useState } from 'react';
import { Camera } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { ReceiptPicker } from '../expenses/ReceiptPicker';
import type { Trip } from '../../types/trip';

/**
 * FE-34 (ADR-032 §3) — «Agregar manifiesto» al viaje EN CURSO.
 *
 * Existe porque la foto puede no haber entrado al iniciar (sin señal, sin el
 * papel a mano, o la subida falló después del `start`) y la UI la exige para
 * finalizar. Es la misma captura que el recibo del gasto; sube al ingreso del
 * viaje (`trip.revenueId`) a través de `adjuntarManifiesto`, que es lo único
 * de ingresos que el Operador puede hacer (#225).
 */
interface Props {
  isOpen: boolean;
  trip: Trip | null;
  onClose: () => void;
  onSubir: (trip: Trip, file: File) => Promise<unknown>;
}

export function TripManifestModal({ isOpen, trip, onClose, onSubir }: Props) {
  const [foto, setFoto] = useState<File | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setFoto(null);
    setError(null);
  }, [isOpen, trip?.id]);

  if (!trip) return null;

  const subir = async () => {
    if (!foto) return;
    setSubiendo(true);
    setError(null);
    try {
      await onSubir(trip, foto);
      onClose();
    } catch (err) {
      const detail = (err as { detail?: string })?.detail;
      setError(detail ?? 'No se pudo subir la foto. Revisa la señal e inténtalo de nuevo.');
    } finally {
      setSubiendo(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Manifiesto · ${trip.vehiclePlate}`}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={subiendo}>Cancelar</Button>
          <Button leftIcon={<Camera size={14} />} onClick={subir} isLoading={subiendo} disabled={!foto}>Subir manifiesto</Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-secondary)' }}>
          {trip.customerName ?? 'Cliente'}{trip.tariffName ? ` · ${trip.tariffName}` : ''} · {trip.origin} → {trip.destination}
        </p>
        <ReceiptPicker file={foto} onChange={setFoto} disabled={subiendo} label="Foto del manifiesto" ocultarOpcional />
        <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>
          Hace falta para finalizar el viaje. Si hay más de una versión, la última es la que vale.
        </p>
        {error && <p style={{ margin: 0, fontSize: '12px', color: 'var(--danger, #dc2626)' }}>{error}</p>}
      </div>
    </Modal>
  );
}
